/**
 * Lightweight structured logger with configurable log levels.
 *
 * Log levels (in order): debug < info < warn < error < silent
 *
 * - Development: defaults to 'debug' (all messages)
 * - Production: defaults to 'warn' (only warn + error)
 * - Override at runtime: logger.setLevel('info')
 * - Override via env: VITE_LOG_LEVEL=debug
 *
 * Named loggers for subsystems:
 *   const log = logger.create('compass')
 *   log.debug('heading updated', { heading: 42 })
 *   // → [compass] heading updated { heading: 42 }
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

const LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
}

function resolveDefaultLevel(): LogLevel {
  // Allow override via Vite env variable
  const envLevel = import.meta.env.VITE_LOG_LEVEL as string | undefined
  if (envLevel && envLevel in LEVELS) {
    return envLevel as LogLevel
  }
  // Fallback: debug in dev, warn in production
  return import.meta.env.DEV ? 'debug' : 'warn'
}

let currentLevel: LogLevel = resolveDefaultLevel()

function shouldLog(level: LogLevel): boolean {
  return LEVELS[level] >= LEVELS[currentLevel]
}

export interface Logger {
  debug(...args: unknown[]): void
  info(...args: unknown[]): void
  warn(...args: unknown[]): void
  error(...args: unknown[]): void
}

function createLogger(prefix?: string): Logger {
  const tag = prefix ? `[${prefix}]` : ''

  return {
    debug(...args: unknown[]) {
      if (shouldLog('debug')) {
        console.log(tag, ...args)
      }
    },
    info(...args: unknown[]) {
      if (shouldLog('info')) {
        console.info(tag, ...args)
      }
    },
    warn(...args: unknown[]) {
      if (shouldLog('warn')) {
        console.warn(tag, ...args)
      }
    },
    error(...args: unknown[]) {
      if (shouldLog('error')) {
        console.error(tag, ...args)
      }
    },
  }
}

/** Root logger (no prefix) */
const rootLogger = createLogger()

export const logger = {
  ...rootLogger,

  /** Create a named logger for a subsystem */
  create(name: string): Logger {
    return createLogger(name)
  },

  /** Get the current log level */
  getLevel(): LogLevel {
    return currentLevel
  },

  /** Change the log level at runtime */
  setLevel(level: LogLevel) {
    currentLevel = level
  },
}
